import React, { useState } from "react";
import {
  Button,
  Card,
  Input,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Row,
  Badge,
  UncontrolledTooltip,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import AdminHeader from "components/Headers/AdminHeader";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import {
  DEFAULT_RECORDS_PER_PAGE,
  STATUS_OPTIONS,
  COURSE_MODE,
} from "components/constant";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import Select, { components } from "react-select";
import { fixedWidthCustomStyle } from "constants/CommonConstants";

/* STATIC DATA (TEMP – later replace with API) */
const sessionList = [
  {
    id: 1,
    sessionName: "React Basics",
    courseName: "Frontend Development",
    sessionType: "Online",
    status: "Pending",
  },
  {
    id: 2,
    sessionName: "Node.js Fundamentals",
    courseName: "Backend Development",
    sessionType: "Offline",
    status: "Submitted",
  },
  {
    id: 3,
    sessionName: "MongoDB Workshop",
    courseName: "Database Management",
    sessionType: "Online + Offline",
    status: "Pending",
  },
  {
    id: 4,
    sessionName: "Advanced JavaScript",
    courseName: "JavaScript Mastery",
    sessionType: "Online",
    status: "Submitted",
  },
];

function SessionsList() {
  const navigate = useNavigate();
  const [pageNum, setPageNum] = useState(1);
  const [searchState, setSearchState] = useState({
    sessionName: "",
    courseName: "",
    status: null,
    mode: null,
    filterParams: [],
  });

  const handleViewSession = (e, row) => {
    e.stopPropagation();

    const encodedId = urlEncodedId(row.id);

    navigate(`/instructor/sessions/view/${encodedId}`, {
      state: {
        sessionName: row.sessionName,
        courseName: row.courseName,
      },
    });
  };

  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "Submitted" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    const filters = [];

    if (searchState.sessionName) {
      filters.push({
        key: "sessionName",
        value: searchState.sessionName,
      });
    }

    if (searchState.courseName) {
      filters.push({
        key: "courseName",
        value: searchState.courseName,
      });
    }

    if (searchState.status) {
      filters.push({
        key: "status",
        value: searchState.status.value,
      });
    }

    if (searchState.mode) {
      filters.push({
        key: "mode",
        value: searchState.mode.value,
      });
    }

    setSearchState((prev) => ({
      ...prev,
      filterParams: filters,
    }));

    if (pageNum !== 1) setPageNum(1);
  };

  const clearSearch = () => {
    setSearchState({
      sessionName: "",
      courseName: "",
      status: null,
      mode: null,
      filterParams: [],
    });

    if (pageNum !== 1) setPageNum(1);
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "id",
    },
    {
      Header: "Session Name",
      accessor: "sessionName",
    },
    {
      Header: "Course Name",
      accessor: "courseName",
    },
    {
      Header: "Session Type",
      accessor: "sessionType",
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <Badge color={value === "Submitted" ? "success" : "warning"} pill>
          {value}
        </Badge>
      ),
    },
    {
      Header: "Actions",
      disableSortBy: true,
      Cell: ({ row }) => {
        const encodedId = urlEncodedId(row.original.id);

        return (
          <div className="actions-right d-flex justify-content-start gap-2">
            <Button
              className="btn-link d-flex align-items-center p-0"
              id={`view${encodedId}`}
              size="sm"
              onClick={(e) => handleViewSession(e, row.original)}
            >
              <i className="fa fa-eye mr-1" />
            </Button>
            <UncontrolledTooltip delay={0} target={`view${encodedId}`}>
              View
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <AdminHeader />
      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <CardTitle tag="h3" className="mb-0">
                  Sessions
                </CardTitle>
              </CardHeader>

              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by session name"
                      className="mr-2 mb-2 filter-input-large"
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      placeholder="Search by course name"
                      className="mr-2 mb-2 filter-input-large"
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={STATUS_OPTIONS}
                      placeholder="Select status"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={COURSE_MODE}
                      placeholder="Select mode"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                    />

                    <Button
                      color="default"
                      // className="d-flex align-items-center btn-deep-blue mb-0"
                      className="d-flex align-items-center mb-2 filter-btn btn-deep-blue"
                    >
                      <i className="fa fa-search mr-1" />
                      Search
                    </Button>
                    {searchState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="ml-2 mb-2 filter-btn btn-simple"
                        onClick={clearSearch}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-2 mb-3 mx-1">
                  <span className="total-session-records">
                    Total Records : {sessionList.length}
                  </span>
                </div>

                <CommonTable
                  columns={columns}
                  data={sessionList}
                  defaultPageSize={DEFAULT_RECORDS_PER_PAGE || 10}
                  activePage={pageNum}
                  setPageNum={setPageNum}
                  totalRecords={sessionList.length}
                  showPaginationBottom={true}
                  rowClick={(row) =>
                    navigate(
                      `/instructor/sessions/view/${urlEncodedId(row.id)}`,
                      {
                        state: {
                          sessionName: row.sessionName,
                          courseName: row.courseName,
                        },
                      },
                    )
                  }
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default SessionsList;
